/**
 * The shop's own day. `LOC-004`, `ACC-002`, `ACC-008`.
 *
 * **The accounting date is the branch's local calendar date, not the server's UTC one**, and the
 * shop was asked directly: a sale rung up at 1am belongs to the day the shopkeeper calls it.
 *
 * Every writer used to derive it as `new Date(now).toISOString().slice(0, 10)`, which is the UTC
 * date. Pakistan is UTC+5, so a Karachi shop's sales between midnight and 05:00 local booked to the
 * previous day: the wrong day's Z-report, the wrong `ACC-008` period at a month boundary, and on
 * 1 July the wrong fiscal year, since `ACC-007a` runs the Pakistani year July to June.
 *
 * **`Intl`, not arithmetic, and not `toLocaleString`.** A fixed offset is wrong for any tenant that
 * ever observes daylight saving, and hand-rolling a zone table here would be a second, staler copy
 * of one the platform already ships. `formatToParts` is used rather than `format` so the digits are
 * read out of the parts as numbers and reassembled, which is what makes `D-03`'s "Western digits
 * everywhere" a property of this function rather than of whichever locale it is handed:
 * `OrderDetail.tsx` records why a locale-driven format is refused for display, and the same reason
 * applies here with more force, because this string reaches the ledger.
 *
 * Pure, so it lives in `domain`: a timezone is data the caller supplies, and looking one up on a
 * branch row is I/O that belongs behind the `db` boundary.
 */
/** Thrown rather than falling back to UTC: a silent fallback is the defect this replaces. */
export class BranchDateError extends Error {
    constructor(message) {
        super(message);
        this.name = 'BranchDateError';
    }
}
/**
 * The calendar date at `instant`, as an IANA timezone sees it, as `YYYY-MM-DD`.
 *
 * @param instant milliseconds since the epoch, as `Date.now()` gives them.
 * @param timeZone an IANA zone, e.g. `Asia/Karachi`. `branch.timezone` holds it.
 */
export function branchDate(instant, timeZone) {
    if (!Number.isFinite(instant)) {
        throw new BranchDateError(`Not an instant: ${String(instant)}`);
    }
    if (timeZone === '') {
        throw new BranchDateError('A branch with no timezone cannot have an accounting date. Set branch.timezone (BR-01).');
    }
    let parts;
    try {
        parts = new Intl.DateTimeFormat('en-US', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            // The Gregorian calendar and Latin digits are stated rather than left to the locale, so the
            // answer cannot change with the runtime's ICU data or the server's environment.
            calendar: 'gregory',
            numberingSystem: 'latn',
        }).formatToParts(new Date(instant));
    }
    catch {
        throw new BranchDateError(`${timeZone} is not a timezone this runtime knows. Branch timezones are IANA names, e.g. Asia/Karachi.`);
    }
    const part = (type) => {
        const found = parts.find((candidate) => candidate.type === type);
        if (found === undefined)
            throw new BranchDateError(`No ${type} in the formatted date.`);
        return found.value;
    };
    // Reassembled from the parts rather than taking the formatted string, so the separator, the field
    // order and the era text of whatever locale is in play cannot reach the ledger.
    return `${part('year').padStart(4, '0')}-${part('month').padStart(2, '0')}-${part('day').padStart(2, '0')}`;
}
/**
 * `ACC-008`'s period, `YYYY-MM`, from a date this module produced.
 *
 * Takes the date rather than the instant on purpose: a period derived straight from a clock is a
 * second place the timezone could be forgotten, and the two would then disagree at every month
 * boundary for five hours.
 */
export function periodOfDate(date) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        throw new BranchDateError(`Not a YYYY-MM-DD date: ${JSON.stringify(date)}`);
    }
    return date.slice(0, 7);
}
/**
 * The shop's wall clock at an instant, as `YYYY-MM-DDTHH:mm`. `RES-001`, `LOC-004`.
 *
 * **The string a `datetime-local` input reads and writes**, which is the only reason this exists in
 * this shape rather than as a formatter. That control has no zone: it hands back whatever a person
 * typed and reads back whatever it is given as a wall clock. So a value produced with
 * `toISOString().slice(0, 16)` is UTC presented as local, and in Karachi it is five hours out, in
 * both directions, with nothing on screen saying so.
 *
 * Paired with `instantFromShopClock`, which is its inverse. They go together: a screen that formats
 * with one and parses with the other round-trips, and a screen that uses one alone is the bug above
 * with extra steps.
 *
 * `branchDate`'s reasoning applies unchanged: `Intl` rather than a fixed offset, because a fixed
 * offset is wrong for any tenant that ever observes daylight saving, and `formatToParts` rather
 * than `format`, so the separators and digits are this function's and not the locale's.
 */
export function shopClock(instant, timeZone) {
    const parts = clockParts(instant, timeZone);
    return (`${parts.year.padStart(4, '0')}-${parts.month.padStart(2, '0')}-${parts.day.padStart(2, '0')}` +
        `T${parts.hour.padStart(2, '0')}:${parts.minute.padStart(2, '0')}`);
}
/**
 * The instant at which the shop's clock reads `wall`, as an ISO string. The inverse of `shopClock`.
 *
 * **Two passes, because the offset depends on the answer.** A wall clock names an instant only
 * once the zone's offset at that instant is known, and the offset is what is being solved for. The
 * first pass guesses with the offset at the UTC reading of the same digits, the second corrects it
 * with the offset at the guessed instant. That converges everywhere except inside a daylight-saving
 * gap, where no instant has that wall clock at all and the second pass lands on the nearest one.
 *
 * Pakistan observes no daylight saving, so one pass would be correct for every tenant today. The
 * second is here because §13's country packs are what make that a fact about Pakistan rather than a
 * property of this function.
 */
export function instantFromShopClock(wall, timeZone) {
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(wall)) {
        throw new BranchDateError(`Not a wall clock: ${wall}. Expected YYYY-MM-DDTHH:mm.`);
    }
    const asIfUtc = Date.parse(`${wall}:00Z`);
    if (!Number.isFinite(asIfUtc))
        throw new BranchDateError(`Not a wall clock: ${wall}.`);
    const guess = asIfUtc - offsetMs(asIfUtc, timeZone);
    return new Date(asIfUtc - offsetMs(guess, timeZone)).toISOString();
}
/** How far ahead of UTC the zone is at `instant`, in milliseconds. */
function offsetMs(instant, timeZone) {
    const p = clockParts(instant, timeZone);
    const asUtc = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour) % 24, Number(p.minute), Number(p.second));
    return asUtc - instant;
}
function clockParts(instant, timeZone) {
    if (!Number.isFinite(instant)) {
        throw new BranchDateError(`Not an instant: ${String(instant)}`);
    }
    if (timeZone === '') {
        throw new BranchDateError('A branch with no timezone cannot have a wall clock. Set branch.timezone (BR-01).');
    }
    let parts;
    try {
        parts = new Intl.DateTimeFormat('en-US', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            // `hourCycle` and not `hour12: false`, which gives 24 for midnight in some ICU versions.
            hourCycle: 'h23',
            calendar: 'gregory',
            numberingSystem: 'latn',
        }).formatToParts(new Date(instant));
    }
    catch {
        throw new BranchDateError(`${timeZone} is not a timezone this runtime knows. Branch timezones are IANA names, e.g. Asia/Karachi.`);
    }
    const part = (type) => {
        const found = parts.find((candidate) => candidate.type === type);
        if (found === undefined)
            throw new BranchDateError(`No ${type} in the formatted date.`);
        return found.value;
    };
    return {
        year: part('year'),
        month: part('month'),
        day: part('day'),
        hour: part('hour'),
        minute: part('minute'),
        second: part('second'),
    };
}
//# sourceMappingURL=branch-date.js.map